import styled from '@emotion/styled';
import { rem } from '@utils';

const DropdownButton = styled.button<{ selected?: boolean }>(
  ({ selected, theme }) => `
  background: ${selected ? theme.palette.primary.dark : 'transparent'};
  border: none;
  border-radius: 4px;
  color: ${theme.palette.common[selected ? 'white' : 'black']};
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: ${rem(8)};
  height: ${rem(32)};
  font-family: inherit;
  font-size: ${rem(14)};
  font-weight: 500;
  margin-left: auto;
  max-width: var(--dataset-dropdown-button-max-width);
  overflow: hidden;
  padding: ${rem(8)};
  transition: ${selected ? '100ms ease' : 'none'};
  transition: 200ms ease;
  user-select: none;

  &:hover {
    color: ${selected ? theme.palette.common.white : theme.palette.primary.main};
  }

  &:disabled {
    color: ${theme.palette.grey['500']};
  }  

  ${DropdownButtonIcon} {
    transform: rotate(${selected ? 180 : 0}deg);
  }
`
);

const DropdownButtonLabel = styled.span`
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const DropdownButtonIcon = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  width: ${rem(16)};
  height: ${rem(16)};
`;

const List = styled.ul<{ alwaysShowScrollbar?: boolean; scrollable?: boolean }>`
  margin-right: ${({ scrollable }) => rem(scrollable ? 4 : 0)};
  overflow-y: ${({ alwaysShowScrollbar, scrollable }) => {
    if (scrollable) {
      if (alwaysShowScrollbar) {
        return 'visible';
      }

      return 'auto';
    }

    return 'hidden';
  }};
  overflow-x: hidden;

  &&::-webkit-scrollbar {
    width: ${rem(8)};
  }

  &&::-webkit-scrollbar-thumb {
    border-width: ${rem(2)};
  }
`;

const ListItem = styled.li<{ selected?: boolean }>(
  ({ selected, theme }) => `
  color: ${theme.palette.common.black};
  cursor: ${selected ? 'default' : 'pointer'};
  display: flex;
  align-items: center;
  flex-flow: row nowrap;
  gap: ${rem(12)};
  padding: ${rem(8)} ${rem(16)};
  user-select: none;

  .action-icon {
    color: ${theme.palette.grey['600']};
    flex-shrink: 0;
    margin-right: ${rem(-4)};
    width: ${rem(16)};
    height: ${rem(16)};
    transition: color 100ms ease;
  }

  .icon {
    flex-shrink: 0;
  }

  *:is(${ListItemTitle}, ${ListItemSubtitle}) {
    transition: color 100ms ease;
  }

  *:is(${ListItemTitle}) {
    color: ${selected ? theme.palette.primary.dark : 'inherit'};
  }

  &:hover *:is(${ListItemTitle}, ${ListItemSubtitle}) {
    ${!selected && `color: ${theme.palette.primary.main};`}
  }

  &:hover .action-icon {
    color: ${theme.palette.primary.main};
  }

  &:focus-visible {
    outline: 2px solid ${theme.palette.primary.dark};
    outline-offset: -2px;
  }

  ${Menu}.dataset-menu & {
    display: grid;
    color: ${theme.palette.grey['600']};
    grid-template: auto auto / auto 1fr;
    gap: ${rem(8)} ${rem(16)};
    padding: ${rem(16)};
  }

  @media (max-width: 430px) {
    .action-icon {
      border-radius: 4px;
      box-shadow: inset 0 0 0 1px ${theme.palette.grey['500']};
      box-sizing: content-box;
      color: ${theme.palette.grey['500']};
      height: ${rem(14)};
      padding: ${rem(8)};
      width: ${rem(14)};
    }

    &:hover .action-icon {
      box-shadow: inset 0 0 0 1px ${theme.palette.primary.main};
    }
  }
`
);

const Menu = styled.menu<{ hidden?: boolean }>(
  ({ hidden, theme }) => `
  background: ${theme.palette.common.white};
  border-radius: 8px;
  box-shadow: 
    0px 0px 4px 0px rgba(0, 0, 0, 0.08), 
    inset 0 0 0 1px ${theme.palette.grey['200']};
  cursor: default;
  display: flex;
  flex-flow: column;
  max-width: var(--dataset-menu-max-width);
  max-height: none;
  overflow-x: hidden;
  opacity: ${hidden ? 0 : 1};
  right: ${rem(36)};
  padding: ${rem(12)} ${rem(8)};
  position: absolute;
  pointer-events: ${hidden ? 'none' : 'auto'};
  transform: scale(${hidden ? 0 : 1});
  transition: 
    opacity 200ms cubic-bezier(0.4, 0, 0.2, 1) ${hidden ? 0 : 200}ms;
  width: 100%;
  will-change: opacity, transform;
  z-index: ${hidden ? -1 : 2};

  @media (max-width: 600px) {
    left: 0;
    max-width: none;
  }
`
);

const ListItemTitle = styled.div`
  font-weight: 600;

  ${Menu} & {
    grid-area: 1 / 2;
  }
`;

const ListItemSubtitle = styled.div`
  color: ${({ theme }) => theme.palette.grey['600']};

  ${Menu} & {
    grid-area: 2 / 2;
  }
`;

const Radio = styled.div<{ checked?: boolean }>(
  ({ checked, theme }) => `
  width: ${rem(16)};
  height: ${rem(16)};
  --radio-color: ${
    checked ? theme.palette.primary.dark : theme.palette.common.black
  }; 
  background: var(--radio-color);   
  border-radius: 50%;
  border: 2px solid var(--radio-color);
  --dot-size: ${checked ? 2.5 : 7}px;
  box-shadow: inset 0 0 0 var(--dot-size) ${theme.palette.common.white};
  transition: 
    background 100ms ease, 
    border-color 100ms ease, 
    box-shadow 100ms ease;

  ${ListItem}:hover & {
    --radio-color: ${
      checked ? theme.palette.primary.dark : theme.palette.primary.main
    };  
    background: var(--radio-color);
    border-color: var(--radio-color);
  }
`
);

export default {
  DropdownButton,
  DropdownButtonIcon,
  DropdownButtonLabel,
  List,
  ListItem,
  ListItemTitle,
  ListItemSubtitle,
  Menu,
  Radio
};
